import { styled } from '@linaria/react';

export const CoatingTable = styled.table`
    width: 100%;
    margin-top: 1rem;
    border-collapse: collapse;
    border: 1px solid #dee2e6;

    thead {
        background: rgb(238 238 238);
        border-bottom: 2px solid #dee2e6;

        th {
            padding: 0.75rem;
            text-align: left;
            font-weight: 700;
            color: #495057;
            font-size: 0.875rem;
            border-right: 1px solid #dee2e6;

            &:last-child {
                border-right: none;
            }
        }
    }

    tbody {
        tr {
            border-bottom: 1px solid #dee2e6;
            transition: background 0.2s;

            &:hover {
                background: #f8f9fa;
            }

            td {
                padding: 0.75rem;
                font-size: 0.875rem;
                color: #333;
                border-right: 1px solid #dee2e6;
                vertical-align: top;

                &:last-child {
                    border-right: none;
                }
            }
        }
    }

    .max-temp-col {
        width: 120px;
    }

    .action-col {
        width: 50px;
    }
`;

export const CoatingName = styled.div`
    display: flex;
    align-items: center;
    height: 25px;
`;

export const CompatibilityBadge = styled.span<{ level: number }>`
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.8rem;

    ${(props) => {
        switch (props.level) {
            case 1: // Yes
                return `
                    background: #d4edda;
                    color: #155724;
                    border: 1px solid #c3e6cb;
                `;
            case 2: // Restrictions
                return `
                    background: #fff3cd;
                    color: #856404;
                    border: 1px solid #ffeaa7;
                `;
            case 3: // No
                return `
                    background: #f8d7da;
                    color: #721c24;
                    border: 1px solid #f5c6cb;
                `;
            default: // Not Assessed
                return `
                    background: #e2e3e5;
                    color: #383d41;
                    border: 1px solid #d6d8db;
                `;
        }
    }}
`;

export const CoatingNotesList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
`;

export const CoatingNoteLink = styled.a`
    color: #007bff;
    text-decoration: none;
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
        text-decoration: underline;
        color: #0056b3;
    }

    &:after {
        content: ',';
        color: #333;
        margin-right: 0.25rem;
    }

    &:last-child:after {
        content: '';
    }
`;

export const ManufacturerCell = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const ManufacturerLink = styled.a`
    color: #007bff;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
        color: #0056b3;
    }
`;

export const ManufacturerName = styled.span`
    color: #333;
`;

export const TemperatureCell = styled.div`
    white-space: nowrap;
`;

export const NoCoatingsMessage = styled.p`
    color: #6c757d;
    font-style: italic;
    margin: 1rem 0;
`;

export const CoatingCommentsSection = styled.div`
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #dee2e6;
`;

export const CommentsLabel = styled.label`
    display: block;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
`;

export const CommentsText = styled.pre`
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: inherit;
    font-size: 0.875rem;
    color: #333;
    margin: 0;
    line-height: 1.6;
`;

export const ManufacturerPopover = styled.div`
    padding: 0.75rem;
    max-width: 300px;

    .popover-header {
        font-weight: 600;
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
        color: #333;
    }

    .popover-content {
        display: flex;
        gap: 1rem;
        align-items: flex-start;
    }

    .logo-section {
        flex-shrink: 0;

        img {
            max-width: 80px;
            max-height: 60px;
            object-fit: contain;
        }
    }

    .links-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        a {
            color: #007bff;
            text-decoration: none;
            font-size: 0.875rem;

            &:hover {
                text-decoration: underline;
            }
        }
    }
`;

export const NotePopover = styled.div`
    padding: 0.5rem;
    max-width: 400px;

    .note-title {
        font-weight: 600;
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
        color: #333;
    }

    .note-text {
        font-size: 0.875rem;
        color: #495057;
        line-height: 1.5;
        white-space: pre-wrap;
    }
`;

// Edit mode styled components
export const EditSelect = styled.select`
    padding: 0.375rem 0.5rem;
    font-size: 0.875rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    color: #495057;
    min-width: 120px;
    cursor: pointer;

    &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &:hover {
        border-color: #adb5bd;
    }
`;

export const EditInput = styled.input`
    padding: 0.375rem 0.5rem;
    font-size: 0.875rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    color: #495057;
    width: 80px;

    &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &:hover {
        border-color: #adb5bd;
    }

    &::placeholder {
        color: #adb5bd;
    }

    /* Hide number input spinners */
    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type='number'] {
        -moz-appearance: textfield;
    }
`;

export const RemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    color: #dc3545;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f8d7da;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    }
`;

export const ActionCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const CategoryLink = styled.a`
    color: #007bff;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
        color: #0056b3;
    }
`;

export const CommentsTextarea = styled.textarea`
    width: 100%;
    padding: 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    color: #495057;
    resize: vertical;
    min-height: 80px;
    font-family: inherit;
    line-height: 1.6;

    &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &:hover {
        border-color: #adb5bd;
    }

    &::placeholder {
        color: #adb5bd;
    }
`;

export const NotesEditContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

export const NoteItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
`;

export const NoteNumber = styled.span`
    font-size: 0.875rem;
    color: #333;
`;

export const NoteSelect = styled.select`
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #fff;
    color: #495057;
    max-width: 150px;
    cursor: pointer;

    &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &:hover {
        border-color: #adb5bd;
    }
`;

export const RemoveNoteButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.125rem 0.25rem;
    font-size: 0.75rem;
    color: #dc3545;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f8d7da;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    }
`;
